// Types derived from authorization-api.yaml — replace with generated types after codegen

import BaseService from '@/services/BaseService';

export type ApplicationCategory = 'APPLICATIONS' | 'TOOLS' | 'BANK_PORTALS';
export type DisplayLocation = 'CARD' | 'MENU' | 'CARD_AND_MENU';
export type LaunchBehavior = 'NEW_TAB';
export type ApplicationStatus = 'ACTIVE' | 'INACTIVE';

export const CATEGORY_LABELS: Record<ApplicationCategory, string> = {
  APPLICATIONS: 'Applications',
  TOOLS: 'Tools',
  BANK_PORTALS: 'Bank Portals',
};

export const EXTERNAL_APPS_READ_PERMISSION = 'authorization_external_apps_read';
export const EXTERNAL_APPS_ADMIN_PERMISSION = 'authorization_external_apps_admin';

export interface AgaApp {
  appId: string;
  appName: string;
}

export interface ExternalApplication {
  id: number;
  name: string;
  url: string;
  description: string;
  category: ApplicationCategory;
  displayLocation: DisplayLocation;
  icon: string;
  launchBehavior: LaunchBehavior;
  status: ApplicationStatus;
  isDefault: boolean;
  isAppScoped: boolean;
  appIds: string[];
  version: number;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
}

export interface DashboardApplication {
  userApplicationId: number;
  catalogApplicationId: number;
  name: string;
  url: string;
  description: string;
  category: ApplicationCategory;
  displayLocation: DisplayLocation;
  icon: string;
  launchBehavior: LaunchBehavior;
  isPrivate: boolean;
  sortOrder: number;
  version: number;
}

export interface DashboardCategory {
  name: ApplicationCategory;
  applications: DashboardApplication[];
}

export interface MyDashboardResponse {
  categories: DashboardCategory[];
}

export interface CreateExternalApplicationRequest {
  name: string;
  url: string;
  description?: string;
  category: ApplicationCategory;
  displayLocation?: DisplayLocation;
  icon?: string;
  launchBehavior?: LaunchBehavior;
  isDefault?: boolean;
  isAppScoped?: boolean;
  appIds?: string[];
}

export interface UpdateExternalApplicationRequest {
  name: string;
  url: string;
  description?: string;
  category: ApplicationCategory;
  displayLocation?: DisplayLocation;
  icon?: string;
  launchBehavior?: LaunchBehavior;
  status?: ApplicationStatus;
  isDefault?: boolean;
  isAppScoped?: boolean;
  appIds?: string[];
  version: number;
}

export interface CreatePrivateApplicationRequest {
  name: string;
  url: string;
  description?: string;
  category: ApplicationCategory;
  icon?: string;
}

export interface UpdatePrivateApplicationRequest {
  name: string;
  url: string;
  description?: string;
  category: ApplicationCategory;
  icon?: string;
  version: number;
}

export interface ReorderApplicationRequest {
  id: number;
  sortOrder: number;
}

class ExternalApplicationService extends BaseService {
  constructor() {
    super('/api/authorization-service');
  }

  getCatalog = async () => {
    return this.get<ExternalApplication[]>({ path: 'external-applications/catalog' });
  };

  getMyDashboard = async () => {
    return this.get<MyDashboardResponse>({ path: 'external-applications/my-dashboard' });
  };

  selectCatalogApp = async (catalogAppId: number) => {
    return this.post<void, DashboardApplication>({
      path: `external-applications/my-dashboard/select/${catalogAppId}`,
      body: undefined,
    });
  };

  removeApp = async (userAppId: number) => {
    return this.delete<void>({ path: `external-applications/my-dashboard/${userAppId}` });
  };

  createPrivateApp = async (request: CreatePrivateApplicationRequest) => {
    return this.post<CreatePrivateApplicationRequest, DashboardApplication>({
      path: 'external-applications/my-dashboard/private',
      body: request,
    });
  };

  updatePrivateApp = async (userAppId: number, request: UpdatePrivateApplicationRequest) => {
    return this.put<UpdatePrivateApplicationRequest, DashboardApplication>({
      path: `external-applications/my-dashboard/private/${userAppId}`,
      body: request,
    });
  };

  reorder = async (entries: ReorderApplicationRequest[]) => {
    return this.put<ReorderApplicationRequest[], void>({
      path: 'external-applications/my-dashboard/reorder',
      body: entries,
    });
  };

  getAllApplications = async (status?: ApplicationStatus) => {
    const path = status ? `external-applications?status=${status}` : 'external-applications';
    return this.get<ExternalApplication[]>({ path });
  };

  createCatalogApp = async (request: CreateExternalApplicationRequest) => {
    return this.post<CreateExternalApplicationRequest, ExternalApplication>({
      path: 'external-applications',
      body: request,
    });
  };

  updateCatalogApp = async (id: number, request: UpdateExternalApplicationRequest) => {
    return this.put<UpdateExternalApplicationRequest, ExternalApplication>({
      path: `external-applications/${id}`,
      body: request,
    });
  };

  deleteCatalogApp = async (id: number) => {
    return this.delete<void>({ path: `external-applications/${id}` });
  };

  getAgaApps = async () => {
    return this.get<AgaApp[]>({ path: 'external-applications/aga-apps' });
  };
}

export default ExternalApplicationService;
